import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useAppDispatch } from 'redux/store';

import { updateUser } from '../redux/slices/auth';
import { useListQuery, FilterConfig } from './useListQuery';
import { getDefaultSortColumn, getDefaultSortOrder, type DefaultSort } from './tableSortDefaults';
import StatusBadge from 'components/base/StatusBadge';
import FormatDateTime, { DateTimeVariant } from 'components/common/FormatDateTime';
import { TABLE_ACTION_REFETCH_DELAY_MS, shouldPauseTablePolling, TABLE_POLL_MS } from 'helpers/tablePolling';

interface UseTableControllerProps {
  userData?: any;
  pageName: string;
  filtersConfig: readonly FilterConfig[];
  headerConfig: readonly any[];
  fetchData: (payload: any) => any;
  getFormattedRow?: (row: any) => any;
  // Declares which columns (by header_key) render via <FormatDateTime> rather
  // than as raw values. The page is the source of truth for this — the
  // server's header_type metadata is incomplete. Rows carry the raw value;
  // the cell formats reactively.
  columnFormats?: Record<string, DateTimeVariant>;
  // Per-column cell renderers, keyed by header_key, for a column the page wants
  // to render itself — a link, say. Declaring it here rather than removing the
  // generated column and appending a bespoke one keeps the column in the
  // chooser, in the user's saved order, and sortable; appending loses all
  // three, silently.
  //
  // Beats both the built-in `status` cell and columnFormats: naming a column
  // here is the more specific statement.
  columnCells?: Record<string, (row: any) => React.ReactNode>;
  // For tables whose rows carry a per-row source timezone (e.g. node
  // instances' scheduled_start_timezone), the header_key to read it from.
  sourceTimezoneKey?: string;
  // header_keys that exist as columns (and in the column chooser) but are
  // hidden until the user opts in. Only applied to users with no saved column
  // preference; once a user saves any selection, their explicit choice governs.
  defaultHiddenColumns?: readonly string[];
  // How the page opens for a user who has never sorted it. Without this the
  // fallback is the FIRST header_key ascending, which is an accident of column
  // order rather than a decision — on a page about what happened, that means
  // alphabetical-by-zone with old and new interleaved.
  //
  // The controller always sends an explicit sortColumn, so the endpoint's own
  // default ORDER BY never gets a chance; this is the only place a page can
  // state one. A saved user preference still wins.
  //
  // Declare it outside the component. An inline literal is a fresh identity on
  // every render, which defeats the memo it feeds.
  defaultSort?: DefaultSort;
}

export const useTableController = ({
  userData,
  pageName,
  filtersConfig,
  headerConfig,
  fetchData,
  getFormattedRow,
  columnFormats,
  columnCells,
  sourceTimezoneKey,
  defaultHiddenColumns = [],
  defaultSort,
}: UseTableControllerProps) => {
  const dispatch = useAppDispatch();

  const { user_settings = {} } = userData || {};
  const { pageSettings } = user_settings || {};
  const { preferredColumns = [], preferredSorting = {} } = pageSettings?.[pageName] || {};

  const selectedColumns = preferredColumns.filter((c: any) => c.checked);
  const preferredColumnOrder = useMemo(() => {
    const map = new Map<string, number>();
    preferredColumns.forEach((column: any, index: number) => {
      map.set(column.header, Number.isInteger(column.order) ? column.order : index);
    });
    return map;
  }, [preferredColumns]);

  const defaultSortColumn = useMemo(() => getDefaultSortColumn(preferredSorting, headerConfig, defaultSort), [preferredSorting, headerConfig, defaultSort]);

  const defaultSortOrder = useMemo(
    () => getDefaultSortOrder(preferredSorting, defaultSort, defaultSortColumn),
    [preferredSorting, defaultSort, defaultSortColumn]
  );

  const {
    filters,
    setFilter,
    setFilterAndSync,
    resetFilters,
    sortColumn,
    sortOrder,
    updateSort,
    page,
    limit,
    updatePage,
    updateLimit,
    payload,
    isHydrated,
    syncUrl,
  } = useListQuery({
    filtersConfig,
    defaultSortColumn,
    defaultSortOrder,
    defaultPageSize: 100,
  });

  const [refreshTick, setRefreshTick] = useState(0);
  const isPollRefreshRef = useRef(false);
  const prevKey = useRef<string>();
  const prevFiltersConfigLength = useRef<number | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const actionRefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const DEBOUNCE_MS = 300;

  const refresh = useCallback(() => {
    isPollRefreshRef.current = false;
    prevKey.current = undefined;
    setRefreshTick((t) => t + 1);
  }, []);

  const triggerActionRefetch = useCallback(() => {
    if (actionRefetchTimerRef.current) clearTimeout(actionRefetchTimerRef.current);
    actionRefetchTimerRef.current = setTimeout(() => {
      isPollRefreshRef.current = false;
      prevKey.current = undefined;
      setRefreshTick((t) => t + 1);
      actionRefetchTimerRef.current = null;
    }, TABLE_ACTION_REFETCH_DELAY_MS);
  }, []);

  const triggerAutoRefresh = useCallback(() => {
    if (shouldPauseTablePolling()) {
      return false;
    }

    isPollRefreshRef.current = true;
    prevKey.current = undefined;
    setRefreshTick((t) => t + 1);
    return true;
  }, []);

  useEffect(() => {
    const currentFiltersLength = filtersConfig?.length ?? 0;
    const filtersJustLoaded = prevFiltersConfigLength.current !== null && prevFiltersConfigLength.current === 0 && currentFiltersLength > 0;

    prevFiltersConfigLength.current = currentFiltersLength;

    const validKeys = (headerConfig || []).map((h: any) => h.header_key);
    const canValidate = Array.isArray(headerConfig) && headerConfig.length > 0;

    const hasInvalidSort = canValidate && payload?.sortColumn && !validKeys.includes(payload.sortColumn);

    // Recovering from a sortColumn the header config does not have; it resets
    // to the same pair the page would have opened on, direction included.
    const safePayload = hasInvalidSort
      ? {
          ...payload,
          sortColumn: defaultSortColumn,
          sortOrder: defaultSortOrder,
          page: 1,
          limit: 100,
        }
      : payload;

    if (hasInvalidSort && isHydrated) {
      syncUrl({
        sortColumn: defaultSortColumn,
        sortOrder: defaultSortOrder,
        page: 1,
        limit: 100,
      });
      updateUserSettings(defaultSortColumn, defaultSortOrder);
      return;
    }

    if (!isHydrated) {
      return;
    }

    if (filtersJustLoaded) {
      return;
    }

    const key = JSON.stringify(safePayload);

    if (prevKey.current === key) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (!prevKey.current) {
      const id = setTimeout(() => {
        prevKey.current = key;
        dispatch(fetchData(safePayload));
      }, 0);

      return () => clearTimeout(id);
    }

    debounceTimerRef.current = setTimeout(() => {
      prevKey.current = key;
      dispatch(fetchData(safePayload));
      debounceTimerRef.current = null;
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [dispatch, payload, headerConfig, isHydrated, syncUrl, defaultSortColumn, defaultSortOrder, filtersConfig, refreshTick]);

  useEffect(() => {
    if (!isHydrated) return;

    pollTimerRef.current = setInterval(() => {
      triggerAutoRefresh();
    }, TABLE_POLL_MS);

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [isHydrated, triggerAutoRefresh]);

  const updateUserSettings = (sortColumn: string, sortOrder: 'asc' | 'desc') => {
    const payload = {
      user_settings: {
        ...user_settings,
        pageSettings: {
          ...pageSettings,
          [pageName]: {
            ...pageSettings?.[pageName],
            preferredSorting: { sortColumn, sortOrder },
          },
        },
      },
    };
    dispatch(updateUser(payload));
  };

  // Dates are formatted reactively in <FormatDateTime> cells, so formatTree no
  // longer touches timestamps — it only applies the page's non-date row
  // transforms and normalizes the tree structure (child_edges defaulting to []
  // is load-bearing for ExpandableRow's refetch detection).
  const formatTree = (rows: readonly any[]): any[] =>
    (rows ?? []).map((node) => {
      const base = getFormattedRow ? getFormattedRow(node) : node;

      const child_nodes = Array.isArray(node.child_nodes) ? formatTree(node.child_nodes) : node.child_nodes;

      const child_edges = node.child_edges ?? [];

      return { ...base, child_nodes, child_edges };
    });

  const getFormattedData = (rawData: readonly any[]) => formatTree(rawData);

  const generateColumns = (onSortClick: (key: string, order: 'asc' | 'desc') => void, functionalColumns: any[] = []) => {
    const baseColumns = headerConfig.map(({ header_key, label }: any) => {
      const fmt = columnFormats?.[header_key];
      const customCell = columnCells?.[header_key];
      return {
        accessorKey: header_key,
        header: label,
        // A page-declared cell wins, being the most specific statement about
        // this column. Otherwise `status` keeps its StatusBadge cell (it is
        // never a datetime), and a declared columnFormats entry renders the raw
        // value through <FormatDateTime>.
        ...(customCell
          ? { cell: ({ row }: any) => customCell(row.original) }
          : header_key === 'status'
            ? {
                cell: ({ getValue, row }: any) =>
                  React.createElement(StatusBadge, {
                    status: row.original.isDeprecated ? 'deprecated' : getValue(),
                  }),
              }
            : fmt
              ? {
                  cell: ({ getValue, row }: any) =>
                    React.createElement(FormatDateTime, {
                      value: getValue(),
                      variant: fmt,
                      sourceTimezone: sourceTimezoneKey ? row.original[sourceTimezoneKey] : undefined,
                    }),
                }
              : {}),
        meta: {
          format: fmt,
          customOnClick: () => {
            const newOrder: 'asc' | 'desc' = sortColumn === header_key && sortOrder === 'asc' ? 'desc' : 'asc';
            onSortClick(header_key, newOrder);
          },
          sortedDirection: sortColumn === header_key ? sortOrder : null,
        },
      };
    });

    const filteredColumns = baseColumns.filter((col) =>
      // No saved preference: show every column except the ones flagged hidden
      // by default. Once the user has saved a selection, honour it exactly.
      selectedColumns.length === 0 ? !defaultHiddenColumns.includes(col.accessorKey) : selectedColumns.some((c: any) => c.header === col.header)
    );

    const orderedColumns =
      preferredColumnOrder.size === 0
        ? filteredColumns
        : [...filteredColumns].sort((a, b) => {
            const orderA = preferredColumnOrder.has(a.header as string) ? preferredColumnOrder.get(a.header as string) : Number.MAX_SAFE_INTEGER;
            const orderB = preferredColumnOrder.has(b.header as string) ? preferredColumnOrder.get(b.header as string) : Number.MAX_SAFE_INTEGER;

            return (orderA ?? Number.MAX_SAFE_INTEGER) - (orderB ?? Number.MAX_SAFE_INTEGER);
          });

    return [...functionalColumns, ...orderedColumns];
  };

  return {
    filters,
    setFilter,
    setFilterAndSync,
    resetFilters,
    sortColumn,
    sortOrder,
    updateSort,
    page,
    limit,
    updatePage,
    updateLimit,
    payload,
    isHydrated,
    syncUrl,
    updateUserSettings,
    getFormattedData,
    generateColumns,
    refresh,
    triggerActionRefetch,
    isSilentRefresh: isPollRefreshRef.current,
  };
};
